import { Fragment, forwardRef, useCallback, useState } from 'react';
import {
  UseVizContainerErrorsErrorsCallback,
  useVizContainerErrorsAndNotices,
  VizContainer,
  VizContainerVizErrorBoundaryResetKeysFn,
} from '@/components';
import { DEFAULT_PANEL_LAYOUT_STRATEGY } from '@/core';
import { SafeMode, useSafeModeEnabled } from '@/plugin-system/runtime';
import { VizPanelProps } from './model';
import { useVizPanelChildrenLayout, useVizPanelLayout } from './useVizPanelLayout';
import { VizPanelContent } from './VizPanelContent';
import { VizPanelHeader, VizPanelHeaderProps } from './VizPanelHeader';
import { VizPanelTimeOverrideBar } from './VizPanelTimeOverrideBar';

/**
 * Building block that is wrapped around all context-aware data visualizations.
 *
 * This component should not be used directly. It is automatically wrapped around data visualizations
 * when using the required `createVisualization` utility.
 */
export const VizPanel = forwardRef<HTMLDivElement, VizPanelProps>(function VizPanel(props: VizPanelProps, ref) {
  const {
    actions,
    collapsible = false,
    title,
    headerVariant = 'subtle',
    width,
    height,
    noPadding = false,
    margin,
    panelDefinition,
    menuItems,
    defaultModalOpen,
    modalOpen,
    onOpenModalButtonClick,
    onCloseModal,
    ContainerModalProps,
    disablePortal,
    panelInspectorContent,
    actionsVisiblity = 'hover',
    draggable,
    layoutStrategy = DEFAULT_PANEL_LAYOUT_STRATEGY,
    panelKey,
    isFetching: isFetchingProp,
    isLoading: isLoadingProp,
  } = props;
  const [collapsed, setCollapsed] = useState(false);

  const vizPanelLayout = useVizPanelLayout({
    width,
    height,
    layoutStrategy,
  });

  // In the `VizPanel` context, the `DataQueriesProvider` is always required and should have an input on the
  // loading/fetching state. These helper functions retain the previous `VizPanel` behavior where passed in
  // loading/fetching state is OR-ed with the state from `DataQueriesProvider` in `VizPanel` after some changes to
  // `VizContainer` to provide more flexibility for composable use cases that do not always require
  // `DataQueriesProvider`.
  const isFetchingFn = useCallback(
    (isFetching: boolean) => {
      return isFetching || Boolean(isFetchingProp);
    },
    [isFetchingProp]
  );
  const isLoadingFn = useCallback(
    (isLoading: boolean) => {
      return isLoading || Boolean(isLoadingProp);
    },
    [isLoadingProp]
  );

  return (
    <VizContainer.Root
      width={vizPanelLayout.container.width}
      height={vizPanelLayout.container.height}
      maxHeight={vizPanelLayout.container.maxHeight}
      margin={margin}
      panelDefinition={panelDefinition}
      variant={headerVariant}
      disablePadding={noPadding}
      actions={actions}
      menuItems={menuItems}
      draggable={draggable}
      collapsed={collapsible ? collapsed : undefined}
      onCollapseButtonClick={collapsible ? setCollapsed : undefined}
      defaultModalOpen={defaultModalOpen}
      modalOpen={modalOpen}
      onOpenModalButtonClick={onOpenModalButtonClick}
      onCloseModal={onCloseModal}
      ContainerModalProps={{
        // Default modal title to the container title
        title,
        // Spread after, so that if the consumer wants to override the modal title, they can.
        ...ContainerModalProps,
      }}
      disablePortal={disablePortal}
      subtleActionsVisibility={actionsVisiblity}
      panelInspectorContent={panelInspectorContent}
      data-testid="viz-panel"
      panelKey={panelKey}
      ref={ref}
      isFetching={isFetchingFn}
      isLoading={isLoadingFn}
    >
      <VizPanelChildren {...props} />
    </VizContainer.Root>
  );
});

interface VizPanelChildrenProps
  extends Omit<VizPanelHeaderProps, 'showHeader' | 'errors' | 'notices'>,
    Pick<
      VizPanelProps,
      | 'children'
      | 'errorBoundaryResetKeys'
      | 'width'
      | 'height'
      | 'validationError'
      | 'onRemoveRelativeTime'
      | 'relativeTimeAdornment'
      | 'enableTimeOverrideBar'
    > {}

/**
 * Component that renders the children of `VizContainer.Root` for `VizPanel`. This is necessary to be a separate
 * component because it depends on some context set in `VizContainer.Root`.
 */
function VizPanelChildren({
  children,
  errorBoundaryResetKeys,
  title,
  width,
  height,
  validationError,
  enableTimeOverrideBar: enableTimeOverrideBarProp,
  onRemoveRelativeTime,
  relativeTimeAdornment,
  ...otherProps
}: VizPanelChildrenProps) {
  const safeModeEnabled = useSafeModeEnabled();

  // If we're in safe mode, wrap the panel content in a SafeMode component
  const ContentWrapper = safeModeEnabled ? SafeMode : Fragment;

  const errorsFn = useCallback<UseVizContainerErrorsErrorsCallback>(
    (defaultErrors) => {
      return {
        // Panel is fully broken when there is a validation error.
        errorState: validationError ? 'critical' : defaultErrors.errorState,
        errors: validationError
          ? [{ errorMessage: validationError.message, count: 1 }, ...defaultErrors.errors]
          : defaultErrors.errors,
      };
    },
    [validationError]
  );

  // This helper function retains the previous `VizPanel` behavior where passed in error boundary reset keys are added
  // to the default keys after some changes to `VizContainer` to provide more flexibility for composable use cases.
  const errorBoundaryResetKeysFn = useCallback<VizContainerVizErrorBoundaryResetKeysFn>(
    (defaultErrorBoundaryResetKeys) => {
      if (!errorBoundaryResetKeys) {
        // If there are no custom keys, just use the defaults.
        return defaultErrorBoundaryResetKeys;
      }

      // Combine the defaults with the custom keys.
      return [...defaultErrorBoundaryResetKeys, ...errorBoundaryResetKeys];
    },
    [errorBoundaryResetKeys]
  );

  // Need to know about errors and notices to determine some layout-related concerns around if we decide to show
  // the header or not. Also needed to handle the validation error.
  const { errors, notices } = useVizContainerErrorsAndNotices({
    errors: errorsFn,
  });

  const vizPanelChildrenLayout = useVizPanelChildrenLayout({
    title,
    hasErrors: !!errors.errors.length,
    hasNotices: !!notices.length,
    showRelativeTimeChip: otherProps.showRelativeTimeChip,
    enableTimeOverrideBar: enableTimeOverrideBarProp,
  });

  return (
    <>
      <VizPanelHeader
        title={title}
        showHeader={vizPanelChildrenLayout.header.show}
        errors={errors}
        notices={notices}
        {...otherProps}
        showRelativeTimeChip={vizPanelChildrenLayout.header.showRelativeTimeChip}
      />
      {vizPanelChildrenLayout.timeOverrideBar.show ? (
        <VizPanelTimeOverrideBar
          onRemoveRelativeTime={onRemoveRelativeTime}
          relativeTimeAdornment={relativeTimeAdornment}
        />
      ) : null}
      <VizContainer.Body>
        <ContentWrapper>
          <VizContainer.VizErrorBoundary errors={errors} errorBoundaryResetKeys={errorBoundaryResetKeysFn}>
            <VizPanelContent vizPanelChildrenLayout={vizPanelChildrenLayout} width={width} height={height}>
              {children}
            </VizPanelContent>
          </VizContainer.VizErrorBoundary>
        </ContentWrapper>
      </VizContainer.Body>
    </>
  );
}
